// src/utils/cityDetector.js
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import { point } from '@turf/helpers'
import { fetchCantonBoundary } from '../services/cantonService'

// Cache para evitar múltiples fetches de boundaries
const boundaryCache = new Map()

// Fallback de slugs válidos (se actualiza dinámicamente)
let validSlugsCache = []

/**
 * Actualiza el cache de slugs válidos
 * Llamar desde CitiesProvider después de cargar las ciudades
 */
export const updateValidSlugs = slugs => {
  if (Array.isArray(slugs) && slugs.length > 0) {
    validSlugsCache = slugs
  }
}

/**
 * Detecta la ciudad actual basándose en la URL
 * @param {string} pathname - pathname de react-router
 * @param {string[]} validSlugs - opcional, lista de slugs válidos
 * @returns {string|null} slug de la ciudad o null
 */
export const detectCityFromURL = (pathname, validSlugs = validSlugsCache) => {
  if (!pathname) return null

  // Buscar cualquier ciudad válida en el pathname
  for (const slug of validSlugs) {
    if (pathname.includes(`/${slug}`)) return slug
  }

  // Por defecto, si estamos en /arbol sin especificar, usar primera ciudad
  if (pathname.includes('/arbol')) return validSlugs[0] || 'loja'

  return null
}

/**
 * Normaliza cualquier estructura GeoJSON a FeatureCollection
 * @param {Object} geoJson - GeoJSON en cualquier formato
 * @returns {Object} FeatureCollection normalizado
 */
const normalizeGeoJSON = geoJson => {
  if (!geoJson) return null

  // Ya es FeatureCollection
  if (geoJson.type === 'FeatureCollection') {
    return geoJson
  }

  // Es un Feature suelto
  if (geoJson.type === 'Feature') {
    return {
      type: 'FeatureCollection',
      features: [geoJson],
    }
  }

  // Es geometría directa (Polygon/MultiPolygon)
  if (geoJson.type === 'Polygon' || geoJson.type === 'MultiPolygon') {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: geoJson,
          properties: {},
        },
      ],
    }
  }

  return null
}

/**
 * Obtiene el GeoJSON de límites según la ciudad (con cache)
 * Ahora usa el API del backend
 * @param {string} citySlug - slug de la ciudad
 * @returns {Promise<Object>} GeoJSON FeatureCollection
 */
export const getCityBoundary = async citySlug => {
  if (!citySlug) return null

  // Verifica el cache primero
  if (boundaryCache.has(citySlug)) {
    return boundaryCache.get(citySlug)
  }

  try {
    // Obtener boundary desde el API
    const geoJson = await fetchCantonBoundary(citySlug)

    if (!geoJson) {
      console.warn(`Boundary no disponible para ${citySlug}`)
      return null
    }

    const normalized = normalizeGeoJSON(geoJson)

    // Guarda en cache
    boundaryCache.set(citySlug, normalized)

    return normalized
  } catch (error) {
    console.error(`Error al procesar boundary de ${citySlug}:`, error)
    return null
  }
}

/**
 * Verifica si un punto está dentro de los límites de una ciudad
 * @param {number[]} coordinates - [lng, lat]
 * @param {string} citySlug - slug de la ciudad
 * @returns {Promise<boolean>}
 */
export const isPointInCityBounds = async (coordinates, citySlug) => {
  if (!coordinates || coordinates.length !== 2) return false
  if (!citySlug) return false

  const boundary = await getCityBoundary(citySlug)

  if (!boundary?.features?.length) return false

  const pt = point(coordinates)

  try {
    // Verifica contra todas las features del boundary
    return boundary.features.some(feature => {
      try {
        return booleanPointInPolygon(pt, feature)
      } catch (err) {
        console.warn('Feature inválido en boundary:', err)
        return false
      }
    })
  } catch (error) {
    console.error('Error al verificar punto en límites:', error)
    return false
  }
}

/**
 * Obtiene los bounds (límites rectangulares) para maxBounds de Mapbox
 * @param {Object} cityConfig - configuración de la ciudad del contexto
 * @returns {Array<Array<number>>|null} [[oeste, sur], [este, norte]]
 */
export const getCityMaxBounds = cityConfig => {
  return cityConfig?.bounds || null
}

/**
 * Detecta a qué ciudad pertenece un punto específico
 * Útil cuando un árbol no está asociado a ninguna URL específica
 * @param {number[]} coordinates - [lng, lat]
 * @param {Array} cities - lista de ciudades del contexto
 * @returns {Promise<string|null>} slug de la ciudad o null
 */
export const detectCityFromCoordinates = async (coordinates, cities) => {
  if (!cities || cities.length === 0) return null

  for (const city of cities) {
    if (await isPointInCityBounds(coordinates, city.slug)) {
      return city.slug
    }
  }

  return null
}

/**
 * Obtiene información completa de la ciudad
 * @param {Object} cityConfig - configuración de la ciudad del contexto
 * @returns {Promise<Object|null>} Información de la ciudad con boundary
 */
export const getCityInfo = async cityConfig => {
  if (!cityConfig) return null

  const boundary = await getCityBoundary(cityConfig.slug)

  return {
    ...cityConfig,
    boundary,
  }
}

/**
 * Pre-carga los boundaries de todas las ciudades habilitadas
 * Útil para llamar al inicio de la app
 * @param {Array} cities - lista de ciudades del contexto
 * @returns {Promise<void>}
 */
export const preloadBoundaries = async cities => {
  if (!cities || cities.length === 0) return
  await Promise.all(cities.map(city => getCityBoundary(city.slug)))
}

/**
 * Limpia el cache de boundaries
 */
export const clearBoundaryCache = () => {
  boundaryCache.clear()
}
